import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'src/domains/common/components/table/table';
import { cn } from 'src/domains/common/components/utils';
import { useAppStateContext } from 'src/AppState/AppState';
import { PaginationTable } from 'src/domains/casino/webview/bonus-engine/common/components/pagination-table/PaginationTable';
import { FilterMenu } from 'src/domains/casino/webview/bonus-engine/campaign-list/filters/Filters';
import { ArrowUpDownIcon } from 'lucide-react';
import {
    CampaignListState,
    validStatuses,
} from 'src/domains/casino/webview/bonus-engine/campaign-list/CampaignList.state';
import { IconButton } from 'src/domains/common/components/button/icon-button';
import { Switch } from 'src/domains/common/components/switch/switch';
import { Select } from 'src/domains/common/components/select/select';
import { CampaignStatusType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { Input } from 'src/domains/common/components/input/input';
import { useCommon } from 'src/AppState/Common';
import { CampaignTableState } from 'src/domains/casino/webview/bonus-engine/campaign-list/campaign-table/CampaignTable.state';
import { CampaignCreatorState } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.state';
import { CampaignStore } from 'src/domains/casino/state/AppCasino/store/campaign-store/Campaign.store';
import { DateTime } from 'src_common/utils/time/time';
import { OptedInAccountsDialog } from 'src/domains/casino/webview/bonus-engine/campaign-list/opted-in-dialog/OptedInAccountsDialog';

function getAvailableStatuses(
    status: CampaignStatusType,
    currentStatus: string,
    campaignStartTime: DateTime | null
): boolean {
    const isDateAfterNow = campaignStartTime === null ? false : campaignStartTime.isAfter(DateTime.current());
    const statusMap: Record<string, CampaignStatusType[]> = {
        draft: ['draft', isDateAfterNow ? 'ready' : 'active'],
        active: ['active', 'paused'],
        paused: ['paused', isDateAfterNow ? 'ready' : 'active'],
        expired: ['expired'],
        ready: ['paused', 'ready'],
    };

    return statusMap[currentStatus]?.includes(status) ?? false;
}

export interface CampaignTablePropsType {
    campaignListState: CampaignListState;
}

export const CampaignTable = observer('CampaignTable', ({ campaignListState }: CampaignTablePropsType) => {
    const { routerBackoffice } = useAppStateContext();
    const common = useCommon();
    const [campaignTableState] = useState(() => new CampaignTableState(common));
    const campaignStore = CampaignStore.get(common);

    const {
        expandAllWithDescription,
        isAllExpanded,
        isExpiredHidden,
        expandedRows,
        paginatedData,
        setSortConfig,
        filter,
        setFilter,
        toggleHideExpired,
        toggleRowExpansion,
        paginationConfig,
    } = campaignListState;

    return (
        <div className='flex flex-col overflow-hidden flex-nowrap min-h-[400px]'>
            <div className='flex items-center justify-between'>
                <div className='flex gap-4'>
                    <Switch
                        label='Hide Expired'
                        labelPosition='right'
                        checked={isExpiredHidden}
                        onChange={toggleHideExpired}
                        dataTest='hide-expired-switch'
                        dataTestSpan='hide-expired-toggle'
                    />

                    <Switch
                        label='Expand All'
                        labelPosition='right'
                        checked={isAllExpanded}
                        onChange={expandAllWithDescription}
                        dataTest='expand-all-switch'
                        dataTestSpan='expand-all-toggle'
                    />
                </div>

                <div className='flex items-center gap-4 py-4'>
                    <Input
                        placeholder='Search'
                        formInputState={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className='max-w-sm py-2'
                        errors={[]}
                        dataTest='search-input'
                    />

                    <FilterMenu
                        campaignListState={campaignListState}
                        submenuPosition='left'
                    />
                </div>
            </div>

            <div className='relative w-full overflow-auto border border-solid rounded-md border-border'>
                <Table className='table-fixed'>
                    <TableHeader className='sticky top-0 bg-white z-[1] shadow-b-shadow'>
                        <TableRow className='!border-b-0'>
                            <TableHead className='!px-2 h-14 w-12'>&nbsp;</TableHead>

                            <TableHead className='py-0 h-14'>
                                <div
                                    className='cursor-pointer flex items-center gap-2.5'
                                    onClick={() => setSortConfig('campaignName')}
                                >
                                    Name
                                    <ArrowUpDownIcon className='size-4' />
                                </div>
                            </TableHead>

                            <TableHead className='py-0 h-14'>
                                <div
                                    className='cursor-pointer flex items-center gap-2.5'
                                    onClick={() => setSortConfig('campaignType')}
                                >
                                    Campaign Type
                                    <ArrowUpDownIcon className='size-4' />
                                </div>
                            </TableHead>

                            <TableHead className='py-0 h-14'>
                                <div
                                    className='cursor-pointer flex items-center gap-2.5'
                                    onClick={() => setSortConfig('rewardName')}
                                >
                                    Reward Type
                                    <ArrowUpDownIcon className='size-4' />
                                </div>
                            </TableHead>

                            <TableHead className='py-0 h-14'>
                                <div
                                    className='cursor-pointer flex items-center gap-2.5'
                                    onClick={() => setSortConfig('startTime')}
                                >
                                    Start Date | Time
                                    <ArrowUpDownIcon className='size-4' />
                                </div>
                            </TableHead>

                            <TableHead className='py-0 h-14'>
                                <div
                                    className='cursor-pointer flex items-center gap-2.5'
                                    onClick={() => setSortConfig('endTime')}
                                >
                                    End Date | Time
                                    <ArrowUpDownIcon className='size-4' />
                                </div>
                            </TableHead>

                            <TableHead className='py-0 h-14 w-[120px]'>
                                <div
                                    className='cursor-pointer flex items-center gap-2.5'
                                    onClick={() => setSortConfig('status')}
                                >
                                    Status
                                    <ArrowUpDownIcon className='size-4' />
                                </div>
                            </TableHead>

                            <TableHead className='py-0 !px-2 h-14 w-[144px]'>Actions</TableHead>
                        </TableRow>
                    </TableHeader>

                    <TableBody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((campaign, index) => {
                                const isExpanded = expandedRows.includes(campaign.id) && campaign.description !== '';
                                const rowClass = index % 2 === 0 ? 'bg-white' : 'bg-muted/50';
                                const isBorder = isExpanded ? 'border-b-0' : 'border-b';
                                const startCampaignDateTime = campaign.startTime?.format('DD/MM/YYYY | HH:mm');
                                const endCampaignDateTime = campaign.endTime?.format('DD/MM/YYYY | HH:mm');
                                const campaignCreator = CampaignCreatorState.get(common, campaign.id);
                                const currentStatus = campaignCreator.campaign?.status;
                                return (
                                    <React.Fragment key={campaign.id}>
                                        <TableRow
                                            className={cn(
                                                rowClass,
                                                isBorder,
                                                'data-[state=selected]:bg-muted border-border border-solid border-t-0 border-l-0 border-r-0'
                                            )}
                                            data-test={`campaign-row-${campaign.id}`}
                                        >
                                            <TableCell className='py-0 !px-2 h-14'>
                                                <IconButton
                                                    className='no-hover'
                                                    variant='ghost'
                                                    size='default'
                                                    onClick={() => toggleRowExpansion(campaign.id)}
                                                    icon={isExpanded ? 'minus' : 'plus'}
                                                    disabled={campaign.description === ''}
                                                    data-test='expand-button'
                                                />
                                            </TableCell>
                                            <TableCell
                                                className='h-14'
                                                data-test='campaign-name'
                                            >
                                                {campaign.campaignName}
                                            </TableCell>
                                            <TableCell
                                                className='h-14'
                                                data-test='campaign-type'
                                            >
                                                {campaign.campaignTypeForView}
                                            </TableCell>
                                            <TableCell
                                                className='capitalize h-14'
                                                data-test='campaign-reward-type'
                                            >
                                                {campaign.bonusRewardTypeForView}
                                            </TableCell>
                                            <TableCell
                                                className='h-14'
                                                data-test='campaign-start-date'
                                            >
                                                {startCampaignDateTime}
                                            </TableCell>
                                            <TableCell
                                                className='h-14'
                                                data-test='campaign-end-date'
                                            >
                                                {endCampaignDateTime}
                                            </TableCell>
                                            <TableCell className='h-14'>
                                                <Select
                                                    key={campaign.status}
                                                    placeholder='Select'
                                                    values={validStatuses
                                                        .filter((status) =>
                                                            getAvailableStatuses(
                                                                status,
                                                                campaign.status,
                                                                campaign.startTime
                                                            )
                                                        )
                                                        .map((clickedStatus) => ({
                                                            value: clickedStatus,
                                                            label:
                                                                clickedStatus.charAt(0).toUpperCase() +
                                                                clickedStatus.slice(1),
                                                            onClick: async (): Promise<void> => {
                                                                if (clickedStatus === 'active') {
                                                                    if (currentStatus === 'paused') {
                                                                        await campaignCreator.updateCampaignStatus(
                                                                            'active'
                                                                        );
                                                                    } else {
                                                                        await campaignCreator.activateCampaignFromList();
                                                                    }
                                                                    await campaignStore.refreshCampaigns();
                                                                }
                                                                if (clickedStatus === 'ready') {
                                                                    if (currentStatus === 'paused') {
                                                                        await campaignCreator.updateCampaignStatus(
                                                                            'ready'
                                                                        );
                                                                    } else {
                                                                        await campaignCreator.activateCampaignFromList();
                                                                    }
                                                                    await campaignStore.refreshCampaigns();
                                                                }
                                                                if (clickedStatus === 'paused') {
                                                                    await campaignCreator.updateCampaignStatus(
                                                                        'paused'
                                                                    );
                                                                    await campaignStore.refreshCampaigns();
                                                                }
                                                            },
                                                        }))}
                                                    formInputState={campaign.statusForm}
                                                    className='w-28'
                                                    isAutoPosition={true}
                                                    errors={[]}
                                                    dataTest='campaign-change-status'
                                                />
                                            </TableCell>
                                            <TableCell className='h-14'>
                                                <div className='flex gap-4'>
                                                    <IconButton
                                                        variant='ghost'
                                                        title='Edit'
                                                        size='sm'
                                                        icon='pencil'
                                                        onClick={() =>
                                                            routerBackoffice.redirectToCampaign({
                                                                type: 'EDIT',
                                                                view: 'general-details',
                                                                campaignId: campaign.id,
                                                            })
                                                        }
                                                        data-test='campaign-edit-button'
                                                    />
                                                    <IconButton
                                                        icon='copy'
                                                        onClick={() =>
                                                            routerBackoffice.redirectToCampaign({
                                                                type: 'NEW',
                                                                view: 'general-details',
                                                                cloneId: campaign.id,
                                                            })
                                                        }
                                                        size='sm'
                                                        title='Copy'
                                                        variant='ghost'
                                                        data-test='campaign-clone-button'
                                                    />

                                                    <IconButton
                                                        className='text-destructive hover:text-destructive'
                                                        icon='trash'
                                                        onClick={() =>
                                                            campaignTableState.handleDeleteCampaign(campaign.id)
                                                        }
                                                        size='sm'
                                                        title='Delete'
                                                        variant='ghost'
                                                        disabled={
                                                            campaign.status === 'active' ||
                                                            campaign.status === 'paused' ||
                                                            campaign.status === 'ready' ||
                                                            campaignTableState.isLoading === campaign.id
                                                        }
                                                        data-test='campaign-delete-button'
                                                    />
                                                </div>
                                            </TableCell>
                                        </TableRow>

                                        {isExpanded && (
                                            <TableRow
                                                className={cn(
                                                    rowClass,
                                                    'overflow-hidden border-border border-solid border-t-0 border-x-0'
                                                )}
                                                dataTest='campaign-details-wrapper'
                                            >
                                                <TableCell
                                                    colSpan={2}
                                                    className='align-top'
                                                >
                                                    <p className='text-muted-foreground'>Campaign Id</p>
                                                    <p
                                                        className='capitalize'
                                                        data-test='campaign-id'
                                                    >
                                                        {campaign.id}
                                                    </p>
                                                </TableCell>
                                                <TableCell
                                                    colSpan={2}
                                                    className='align-top'
                                                >
                                                    <p className='text-muted-foreground'>Player Type</p>
                                                    <p
                                                        className='capitalize'
                                                        data-test='campaign-player-type'
                                                    >
                                                        {campaign.playerType === null
                                                            ? '-'
                                                            : campaign.playerType?.replaceAll('-', ' ')}
                                                    </p>
                                                </TableCell>
                                                <TableCell
                                                    colSpan={2}
                                                    className='align-top'
                                                >
                                                    <p className='text-muted-foreground'>Is Opt-in</p>
                                                    {campaign.optInOnly === true ? (
                                                        <OptedInAccountsDialog
                                                            campaignId={campaign.id}
                                                            data-test='campaign-is-opt-in'
                                                        />
                                                    ) : (
                                                        <p data-test='campaign-is-opt-in'>No</p>
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    colSpan={2}
                                                    className='align-top'
                                                >
                                                    <p className='text-muted-foreground'>Terms & Conditions</p>
                                                    <p
                                                        data-test='campaign-terms-conditions'
                                                        className='line-clamp-5'
                                                        dangerouslySetInnerHTML={{ __html: campaign.description }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className='h-24 text-center'
                                    data-test='no-results'
                                >
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <PaginationTable config={paginationConfig} />
        </div>
    );
});
